import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { API_BASE_URL, fetchJson } from './api';
import { computeProgrammeDurationMonths } from './financialSchedule';
import UKProjectMap from './UKProjectMap';
import { loadGoogleMapsApi as loadSharedGoogleMapsApi, requestProjectRoute } from './googleMapsRouting';

const GOOGLE_MAPS_API_KEY = 'AIzaSyB8aHKsvXwcOfduoR86RY1jNw1nAK_xn34';
const COMPLETED_KEY = 'monsurv_completed_projects';

function getAuthHeaderOnly() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function loadGoogleMapsApi() {
    return loadSharedGoogleMapsApi(GOOGLE_MAPS_API_KEY);
}

function parseMoneyLoose(value) {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const raw = String(value || '').trim();
    if (!raw) return 0;
    const normalized = raw.replace(/[^0-9.-]/g, '');
    if (!normalized || normalized === '-' || normalized === '.') return 0;
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
}

function parseDurationLoose(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const raw = String(value || '').trim();
    if (!raw) return null;
    const n = Number(raw.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : null;
}

function toFiniteOrNull(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function isLikelyUkCoordinate(lat, lng) {
    const la = Number(lat);
    const ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return false;
    // Broad UK bounding box to discard corrupted stored coordinates.
    return la >= 49 && la <= 61.5 && ln >= -11 && ln <= 3;
}

function formatInteger(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return Math.round(n).toLocaleString('en-GB');
}

function formatCurrency(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '£0';
    return `£${Math.round(n).toLocaleString('en-GB')}`;
}

function formatMonths(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0.0 Months';
    return `${n.toFixed(1)} Months`;
}

function normalizeOffice(raw, index) {
    const value = raw && typeof raw === 'object' ? raw : {};
    return {
        id: String(value.id || `office_${index + 1}`).trim() || `office_${index + 1}`,
        office_location: String(value.office_location || '').trim(),
        address: String(value.address || '').trim()
    };
}

function normalizeOfficeMapSettings(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const legacyOffices = (value.office_location || value.address || value.pin_color)
        ? [value]
        : [];
    const offices = (Array.isArray(value.offices) ? value.offices : legacyOffices)
        .map((office, index) => normalizeOffice(office, index))
        .filter((office) => office.office_location || office.address);
    return {
        offices,
        primary_office_id: String(value.primary_office_id || offices[0]?.id || '').trim()
    };
}

function getCompletedByRef() {
    try {
        const raw = localStorage.getItem(COMPLETED_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function normalizeProject(row) {
    const reference = String(row?.reference || '').trim();
    const projectName = String(row?.project_name || row?.name || '').trim();
    const siteAddress = String(row?.site_address || row?.address || '').trim();
    const lat = Number(row?.site_latitude ?? row?.site_lat ?? row?.latitude ?? row?.lat ?? null);
    const lng = Number(row?.site_longitude ?? row?.site_lng ?? row?.longitude ?? row?.lng ?? row?.lon ?? null);
    const facility = parseMoneyLoose(row?.project_facility ?? row?.facility_amount ?? row?.facility ?? row?.total_facility ?? null);
    const fallbackDuration = parseDurationLoose(row?.project_duration ?? row?.duration_months ?? null);

    const normalizedLat = isLikelyUkCoordinate(lat, lng) ? lat : null;
    const normalizedLng = isLikelyUkCoordinate(lat, lng) ? lng : null;

    return {
        reference,
        name: projectName,
        address: siteAddress,
        lat: Number.isFinite(normalizedLat) ? normalizedLat : null,
        lng: Number.isFinite(normalizedLng) ? normalizedLng : null,
        facility,
        fallbackDuration
    };
}

function mapDestinationForDistance(project) {
    if (project?.address) return project.address;
    if (Number.isFinite(project?.lat) && Number.isFinite(project?.lng)) {
        return `${Number(project.lat)},${Number(project.lng)}`;
    }
    return '';
}

function buildProjectLabel(project) {
    const ref = String(project?.reference || '').trim();
    const name = String(project?.name || '').trim();
    if (ref && name) return `(${ref}) ${name}`;
    return ref || name || 'Unknown Project';
}

export default function ActiveProjectsAnalytics() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeProjects, setActiveProjects] = useState([]);
    const [officeSettings, setOfficeSettings] = useState({ offices: [], primary_office_id: '' });
    const [avgRuntimeMonths, setAvgRuntimeMonths] = useState(0);
    const [distanceLoading, setDistanceLoading] = useState(false);
    const [furthestProject, setFurthestProject] = useState(null);
    const [distanceError, setDistanceError] = useState('');

    function deriveFacilityFromInitialReport(initialReportJson, projectFallback = 0) {
        const tables = initialReportJson?.tables || {};
        const costAssessments = tables?.ini_section_7_cost_assessments?.[0] || null;
        const costs = tables?.ini_section_7_costs?.[0] || null;

        const facilityConfirmed = String(costAssessments?.facility_confirmed || '').trim().toLowerCase();
        const isConfirmed = ['1', 'true', 'yes', 'y', 'on'].includes(facilityConfirmed);
        const confirmedAmount = toFiniteOrNull(
            String(costAssessments?.facility_amount ?? '').trim().replace(/[^0-9.-]/g, '')
        );
        if (isConfirmed && Number.isFinite(confirmedAmount) && confirmedAmount > 0) {
            return confirmedAmount;
        }

        const totalDevelopmentCost = toFiniteOrNull(costs?.total_development_cost);
        if (Number.isFinite(totalDevelopmentCost) && totalDevelopmentCost > 0) {
            return totalDevelopmentCost;
        }

        const fallback = Number(projectFallback || 0);
        return Number.isFinite(fallback) && fallback > 0 ? fallback : 0;
    }

    function deriveAddressFromInitialReport(initialReportJson, fallbackAddress = '') {
        const location = initialReportJson?.tables?.ini_section_3_location?.[0] || null;
        const latestAddress = String(location?.site_address || location?.address || '').trim();
        if (latestAddress) return latestAddress;
        return String(fallbackAddress || '').trim();
    }

    useEffect(() => {
        let cancelled = false;

        async function loadActiveProjects() {
            setLoading(true);
            setError('');

            try {
                const [rawProjects, officeResp] = await Promise.all([
                    fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET', headers: getAuthHeaderOnly() }),
                    fetch(`${API_BASE_URL}/settings/office-map`, {
                        method: 'GET',
                        headers: getAuthHeaderOnly()
                    }).then(async (res) => {
                        const json = await res.json().catch(() => ({}));
                        return res.ok ? json : { settings: {} };
                    })
                ]);

                const completedByRef = getCompletedByRef();
                const rows = Array.isArray(rawProjects) ? rawProjects : [];
                const filtered = rows
                    .map(normalizeProject)
                    .filter((p) => p.reference)
                    .filter((p) => !completedByRef?.[p.reference]);

                if (cancelled) return;
                setActiveProjects(filtered);
                setOfficeSettings(normalizeOfficeMapSettings(officeResp?.settings || {}));

                if (!filtered.length) {
                    setAvgRuntimeMonths(0);
                    return;
                }

                const authHeader = getAuthHeaderOnly();
                const enriched = await Promise.all(filtered.map(async (project) => {
                    try {
                        const url = `${API_BASE_URL}/project/${encodeURIComponent(project.reference)}/initial-report?mode=surveyor`;
                        const json = await fetchJson(url, { method: 'GET', headers: authHeader });

                        const programmeRow = json?.tables?.ini_section_8_programme?.[0] || null;
                        const duration = computeProgrammeDurationMonths(programmeRow);
                        const latestDuration = Number.isFinite(duration) && duration > 0
                            ? Number(duration)
                            : (Number.isFinite(project.fallbackDuration) && project.fallbackDuration > 0 ? Number(project.fallbackDuration) : null);

                        const latestFacility = deriveFacilityFromInitialReport(json, project.facility);
                        const latestAddress = deriveAddressFromInitialReport(json, project.address);

                        return {
                            ...project,
                            facility: latestFacility,
                            address: latestAddress,
                            runtimeMonths: latestDuration
                        };
                    } catch {
                        // Fallback when project-level report payload is unavailable.
                    }
                    return {
                        ...project,
                        runtimeMonths: Number.isFinite(project.fallbackDuration) && project.fallbackDuration > 0
                            ? Number(project.fallbackDuration)
                            : null
                    };
                }));

                if (cancelled) return;

                setActiveProjects(enriched);

                const validDurations = enriched
                    .map((project) => Number(project?.runtimeMonths || 0))
                    .filter((value) => Number.isFinite(value) && value > 0);
                const avg = validDurations.length
                    ? (validDurations.reduce((sum, value) => sum + Number(value), 0) / validDurations.length)
                    : 0;
                setAvgRuntimeMonths(avg);
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message || 'Failed to load active projects');
                    setActiveProjects([]);
                    setAvgRuntimeMonths(0);
                    setOfficeSettings({ offices: [], primary_office_id: '' });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadActiveProjects();

        function handleStorage(event) {
            if (event?.key === COMPLETED_KEY) loadActiveProjects();
        }

        function handleFocus() {
            loadActiveProjects();
        }

        window.addEventListener('storage', handleStorage);
        window.addEventListener('focus', handleFocus);
        return () => {
            cancelled = true;
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function loadFurthestProject() {
            const offices = Array.isArray(officeSettings?.offices) ? officeSettings.offices : [];
            if (!activeProjects.length || !offices.length) {
                setFurthestProject(null);
                setDistanceLoading(false);
                setDistanceError('');
                return;
            }

            setDistanceLoading(true);
            setDistanceError('');
            setFurthestProject(null);
            try {
                await loadGoogleMapsApi();
                const originEntries = offices
                    .map((office) => String(office?.address || office?.office_location || '').trim())
                    .filter(Boolean);

                const projectEntries = activeProjects
                    .map((project) => ({
                        project,
                        destination: mapDestinationForDistance(project)
                    }))
                    .filter((entry) => entry.destination);

                if (originEntries.length !== offices.length || projectEntries.length !== activeProjects.length) {
                    if (!cancelled) setDistanceError('Some offices or projects are missing locations. The furthest-project metric is unavailable until all locations can be compared.');
                    return;
                }

                const minMilesByRef = new Map();
                let failedRoutes = 0;

                for (const origin of originEntries) {
                    for (const entry of projectEntries) {
                        if (cancelled) return;
                        try {
                            const response = await requestProjectRoute({
                                origin,
                                destination: entry.destination,
                                distanceOnly: true,
                                legacyKey: GOOGLE_MAPS_API_KEY
                            });
                            const meters = response.distanceMeters;
                            const miles = meters / 1609.344;
                            const ref = entry.project.reference;
                            const prev = minMilesByRef.get(ref);
                            if (!Number.isFinite(prev) || miles < prev) {
                                minMilesByRef.set(ref, miles);
                            }
                        } catch {
                            failedRoutes += 1;
                        }
                    }
                }

                if (cancelled) return;
                if (failedRoutes) {
                    setDistanceError(`${failedRoutes} office/project routes could not be calculated. The furthest-project metric is unavailable until all routes can be compared.`);
                    return;
                }

                let furthest = null;
                activeProjects.forEach((project) => {
                    const miles = minMilesByRef.get(project.reference);
                    if (!Number.isFinite(miles)) return;
                    if (!furthest || miles > furthest.miles) {
                        furthest = { ...project, miles };
                    }
                });

                setFurthestProject(furthest);
            } catch (e) {
                if (!cancelled) {
                    setFurthestProject(null);
                    setDistanceError(e?.message || 'Project travel distances could not be calculated.');
                }
            } finally {
                if (!cancelled) setDistanceLoading(false);
            }
        }

        loadFurthestProject();
        return () => {
            cancelled = true;
        };
    }, [activeProjects, officeSettings]);

    const totalFacility = useMemo(
        () => activeProjects.reduce((sum, project) => sum + Number(project.facility || 0), 0),
        [activeProjects]
    );

    const largestFacilityProject = useMemo(() => {
        if (!activeProjects.length) return null;
        return activeProjects.reduce((largest, current) => {
            const largestValue = Number(largest?.facility || 0);
            const currentValue = Number(current?.facility || 0);
            return currentValue > largestValue ? current : largest;
        }, activeProjects[0]);
    }, [activeProjects]);

    const metrics = useMemo(() => {
        return [
            {
                label: 'No. of Active Projects',
                value: formatInteger(activeProjects.length)
            },
            {
                label: 'Total Facility Monitored',
                value: formatCurrency(totalFacility)
            },
            {
                label: 'Largest Facility Project',
                value: largestFacilityProject
                    ? `${formatCurrency(largestFacilityProject.facility)} - ${buildProjectLabel(largestFacilityProject)}`
                    : 'Unavailable'
            },
            {
                label: 'Avg. Project Runtime',
                value: formatMonths(avgRuntimeMonths)
            },
            {
                label: 'Furthest Active Project',
                value: furthestProject
                    ? `${formatInteger(furthestProject.miles)} Miles - ${buildProjectLabel(furthestProject)}`
                    : (distanceLoading ? 'Calculating…' : 'Unavailable')
            }
        ];
    }, [activeProjects.length, totalFacility, largestFacilityProject, avgRuntimeMonths, furthestProject, distanceLoading]);

    return (
        <Box sx={{ mt: 1, display: 'grid', gap: 2.5 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {distanceError ? <Alert severity="warning">{distanceError}</Alert> : null}

            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    alignItems: 'stretch',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.25,
                        minHeight: 620,
                        borderRadius: 4,
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(246,249,252,0.98) 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
                        <Box>
                            <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'text.secondary', fontWeight: 800 }}>
                                Portfolio Coverage
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                Active Project Metrics
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Live summary across all active projects currently tracked in the dashboard.
                            </Typography>
                        </Box>
                        <Chip label={`${activeProjects.length} active`} variant="outlined" />
                    </Stack>

                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 520,
                            borderRadius: 3,
                            border: '1px solid rgba(148, 163, 184, 0.18)',
                            background: 'rgba(255,255,255,0.72)',
                            p: 1.5,
                            display: 'grid',
                            gap: 1.15,
                            alignContent: 'start'
                        }}
                    >
                        {loading ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">Loading active project metrics…</Typography>
                            </Stack>
                        ) : metrics.map((metric) => (
                            <Box
                                key={metric.label}
                                sx={{
                                    borderRadius: 2.5,
                                    border: '1px solid rgba(148, 163, 184, 0.2)',
                                    background: 'rgba(255,255,255,0.92)',
                                    px: 1.4,
                                    py: 1.1,
                                    display: 'grid',
                                    gap: 0.25
                                }}
                            >
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.03em' }}>
                                    {metric.label}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 800 }}>
                                    {metric.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        p: 2.25,
                        minHeight: 620,
                        borderRadius: 4,
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.86) 0%, rgba(246,249,252,0.98) 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
                        <Box>
                            <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'text.secondary', fontWeight: 800 }}>
                                Geographic Coverage
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                Active Projects Map
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                All active projects shown on the shared office-map surface.
                            </Typography>
                        </Box>
                        <Chip icon={<PlaceOutlinedIcon />} label={`${activeProjects.length} project${activeProjects.length === 1 ? '' : 's'}`} variant="outlined" />
                    </Stack>

                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 520,
                            borderRadius: 3,
                            overflow: 'hidden',
                            border: '1px solid rgba(148, 163, 184, 0.18)',
                            background: 'rgba(255,255,255,0.7)'
                        }}
                    >
                        <UKProjectMap
                            projects={activeProjects}
                            officeSettings={officeSettings}
                            fillHeight
                            minHeight={520}
                        />
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}
